import { useEffect, useMemo, useState } from 'react';
import { AlertCircle } from 'lucide-react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/shadcn/components/ui/tooltip';
import { useDataLoader } from '../providers/DataLoaderProvider';
import { generateSlug } from '../utils/slugUtils';

/**
 * Displays a slug value, always generating it from the name
 * Can optionally access saved data if sliceId is provided
 *
 * @param {Object} props
 * @param {string} props.name - The name to generate a slug from
 * @param {string} [props.sliceId] - Optional slice ID to look up saved data for comparison
 * @returns {JSX.Element} SlugDisplay component
 */
function SlugDisplay({ name, onChange, sliceId }) {
    const { config } = useDataLoader();
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    // Get saved slice data if sliceId is provided
    const savedSlice = useMemo(
        () => (sliceId && config?.slices?.find((s) => s.id === sliceId)) || null,
        [sliceId, config?.slices],
    );

    const savedSlug = savedSlice?.slug;
    const displaySlug = generateSlug(name) || '';

    const handleTooltipOpenChange = (open) => {
        if (open && !copied) {
            setTooltipOpen(true);
        } else if (!open) {
            setTooltipOpen(false);
        }
    };

    const handleCopy = async () => {
        if (!displaySlug) return;
        try {
            await navigator.clipboard.writeText(displaySlug);
            setCopied(true);
            setTooltipOpen(true);
            setTimeout(() => {
                setTooltipOpen(false);
                setTimeout(() => {
                    setCopied(false);
                }, 500);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    useEffect(() => {
        if (onChange) {
            onChange({ slug: displaySlug });
        }
    }, [displaySlug]);

    return (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {!savedSlug ? (
                <>
                    <AlertCircle className="h-3 w-3 fill-red-500 text-red-500" /> Slug: No slug saved.
                </>
            ) : (
                <TooltipProvider>
                    <Tooltip open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors cursor-pointer disabled:cursor-default disabled:opacity-50"
                                onClick={handleCopy}
                                disabled={!displaySlug || !savedSlug}
                            >
                                Slug: {displaySlug || 'No slug generated'}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent className="text-sm">{copied ? 'Copied!' : 'Click to copy'}</TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
}

export default SlugDisplay;
